interface OpenPayCardData {
  card_number: string;
  holder_name: string;
  expiration_year: string;
  expiration_month: string;
  cvv2: string;
}

interface OpenPayTokenResponse {
  data: {
    id: string;
    card: {
      card_number: string;
      brand: string;
    };
  };
}

interface OpenPayErrorResponse {
  data?: { description?: string };
  message?: string;
}

interface OpenPayInstance {
  setId: (id: string) => void;
  setApiKey: (key: string) => void;
  setSandboxMode: (sandbox: boolean) => void;
  deviceData: {
    setup: (formId: string, hiddenFieldName: string) => string;
  };
  token: {
    create: (
      cardData: OpenPayCardData,
      onSuccess: (response: OpenPayTokenResponse) => void,
      onError: (response: OpenPayErrorResponse) => void,
    ) => void;
  };
}

declare global {
  interface Window {
    OpenPay?: OpenPayInstance;
  }
}

let initialized = false;

export function initializeOpenPay(): OpenPayInstance {
  if (typeof window === "undefined" || !window.OpenPay) {
    throw new Error("OpenPay SDK no está disponible");
  }

  const merchantId = import.meta.env.VITE_APP_OPENPAY_MERCHANT_ID;
  const publicApiKey = import.meta.env.VITE_APP_OPENPAY_PUBLIC_API_KEY;
  if (!merchantId || !publicApiKey) {
    throw new Error("Configuración de OpenPay incompleta");
  }

  const openPay = window.OpenPay;
  if (!initialized) {
    openPay.setId(merchantId);
    openPay.setApiKey(publicApiKey);
    openPay.setSandboxMode(import.meta.env.VITE_APP_ENV === "development");
    initialized = true;
  }
  return openPay;
}

export function setupDeviceSession(
  formId: string = "payment-form",
): string {
  const openPay = initializeOpenPay();
  return openPay.deviceData.setup(formId, "deviceIdHiddenFieldName");
}

export function tokenizeCard(cardData: OpenPayCardData): Promise<{
  token_id: string;
  last4: string;
  brand: string;
}> {
  const openPay = initializeOpenPay();
  return new Promise((resolve, reject) => {
    openPay.token.create(
      cardData,
      (response) => {
        resolve({
          token_id: response.data.id,
          last4: response.data.card.card_number.slice(-4),
          brand: response.data.card.brand,
        });
      },
      (response) => {
        const errorMsg =
          response.data?.description ||
          response.message ||
          "Error al procesar la tarjeta";
        reject(new Error(errorMsg));
      },
    );
  });
}

export type { OpenPayCardData };
